import { useState } from 'react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';

import { Button, Container, Icon, Link, SectionText } from '@/atoms';
import { Card } from '@/molecules';

import type { TypeTextColorVariant } from '@/components/types';
import type { FunctionComponent } from 'react';
import type { TypeNewOpeningProps } from './types';

const VARIANT_MAPS: Record<TypeTextColorVariant, string> = {
  primary: 'text-primary-400 ',
  secondary: 'text-secondary-400 ',
  alternative: 'text-white ',
};

const PopUpBookingLavanda = dynamic(
  () => import('@/organisms/pop-up-booking-lavanda'),
  {
    ssr: false,
  }
);

/**
 * NewOpening
 */
export const NewOpening: FunctionComponent<TypeNewOpeningProps> = ({
  className = '',
  cardProps,
  caption,
  desc,
  title,
  link,
  backgroundColorBottom,
  backgroundColorBottomMobile,
  backgroundColorTop,
  backgroundColorTopMobile,
  cutLevel = 50,
  cutLevelMobile = 50,
  textColor,
  subTitle,
  index,
  accommodationSelect,
  buildingSelect,
  id,
}: TypeNewOpeningProps) => {
  const [formOpen, setFormOpen] = useState(false);

  const popUpToggle = () => {
    setFormOpen(!formOpen);
  };

  return (
    <>
      <PopUpBookingLavanda
        apartmentsByBuilding={accommodationSelect}
        buildings={buildingSelect}
        show={formOpen}
        onClose={popUpToggle}
      />
      <Container
        index={index}
        backgroundColorBottom={backgroundColorBottom}
        backgroundColorTopMobile={backgroundColorTopMobile}
        backgroundColorTop={backgroundColorTop}
        backgroundColorBottomMobile={backgroundColorBottomMobile}
        cutLevel={cutLevel === null ? 50 : cutLevel}
        cutLevelMobile={cutLevelMobile === null ? 50 : cutLevel}
        className={`flex flex-col gap-5 ${className}`}
        id={id}
      >
        <div className=" flex  w-full flex-col gap-6 md:flex-col  md:gap-14   lg:flex-row ">
          <SectionText
            textAlign="start"
            variant={textColor}
            direction="column"
            desc={desc}
            title={title}
            caption={caption}
            subtitle={subTitle}
            className="w-full lg:w-1/3"
          />
          <div className="w-full lg:w-2/3">
            <Card
              className={`w-full rounded-md bg-white ${VARIANT_MAPS[textColor]}`}
              orientation="row"
              classNames={{ content: 'lg:w-1/2' }}
              multimediaProps={{
                images: cardProps.galery,
                roundedPosition: 'left',
              }}
              multimediaHeith={200}
            >
              <div className=" flex flex-col gap-4">
                {cardProps.caption && (
                  <p className="text-caption text-caption--small">
                    {cardProps.caption}
                  </p>
                )}
                {cardProps.title && (
                  <h4 className="text-heading text-heading--h4">
                    {cardProps.title}
                  </h4>
                )}
                {cardProps.subTitle && (
                  <span className="text-body text-body--secondary text-body--secondary--bold">
                    {cardProps.subTitle}
                  </span>
                )}
                {cardProps.desc && (
                  <div className="inline-block">
                    <p>{cardProps.desc}</p>
                  </div>
                )}
                {cardProps.characteristics && (
                  <div className="flex flex-col justify-between gap-3 text-primary-400">
                    {cardProps.characteristics.map(
                      (
                        carac: {
                          icon: string;
                          label: string;
                        },
                        index: number
                      ) => (
                        <div
                          className="flex flex-row items-center gap-2"
                          key={index}
                        >
                          <Icon src={carac.icon} height={25} width={25} />
                          {carac.icon === 'CallCircle' ? (
                            <Link
                              variant="primary"
                              underline
                              className="text-body text-body--secondary font-normal"
                              href={`tel:${carac.label}`}
                            >
                              {carac.label}
                            </Link>
                          ) : (
                            <span className="text-body text-body--secondary font-normal ">
                              {carac.label}
                            </span>
                          )}
                        </div>
                      )
                    )}
                  </div>
                )}

                {(cardProps?.button?.src || cardProps?.link?.text) && (
                  <>
                    <div className="h-px w-full bg-primary-100" />
                    <div className="flex flex-col items-center justify-center gap-4">
                      {cardProps.disabled && cardProps?.button?.text ? (
                        <Button
                          className="w-full"
                          variant="primary"
                          size="large"
                          disabled={true}
                        >
                          {cardProps.button.text}
                        </Button>
                      ) : (
                        cardProps?.button?.src && (
                          <NextLink
                            href={cardProps.button.src}
                            passHref
                            prefetch={false}
                          >
                            <Button
                              className="w-full"
                              variant="primary"
                              size="large"
                              as="a"
                            >
                              {cardProps.button.text}
                            </Button>
                          </NextLink>
                        )
                      )}
                      {cardProps?.link?.text && !cardProps.disabled && (
                        <Link
                          as="button"
                          variant="primary"
                          onClick={popUpToggle}
                        >
                          {cardProps.link.text}
                        </Link>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>

        <>
          {link?.text && link?.ref && (
            <NextLink passHref prefetch={false} href={link.ref}>
              <div className="flex w-full items-start justify-center overflow-hidden md:justify-end lg:justify-start ">
                <Link variant="primary" arrow>
                  {link.text}
                </Link>
              </div>
            </NextLink>
          )}
        </>
      </Container>
    </>
  );
};

NewOpening.displayName = 'NewOpening';
